/**
 * CREDIA API Client
 * Type-safe API client with automatic token refresh and error handling
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { safeExternalRedirect } from '../lib/safeRedirect';
import { useUIStore } from '../stores/uiStore';

/**
 * Raw wire payload at a backend-shape normalization boundary. The mapping
 * code that consumes it (field renames, `??` fallbacks) is the validation.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RawWire = Record<string, any>;

/** FastAPI error convention: { detail: string }. Undefined for non-API errors. */
export function apiErrorDetail(error: unknown): string | undefined {
  return axios.isAxiosError(error) ? error.response?.data?.detail : undefined;
}

/**
 * Human-readable message from an API error, for toasts.
 * `detail` is usually a string, but some endpoints (e.g. 409 already_subscribed)
 * return `{ error, message }` — unwrap `.message` in that case. Falls back to
 * `fallback` for network errors or shapes we don't recognize.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const detail = apiErrorDetail(error);
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (
    detail &&
    typeof detail === 'object' &&
    typeof (detail as { message?: unknown }).message === 'string'
  ) {
    return (detail as { message: string }).message;
  }
  return fallback;
}
import type {
  AccountUsageResponse,
  AuthResponse,
  User,
  ProfileUpdate,
  Deal,
  DealCreate,
  DealUpdate,
  DealsResponse,
  AskLatestResponse,
  AskPreviewResponse,
  AskLockRequest,
  AskCreateResponse,
  MirRatesResponse,
  BankListItem,
  TenderLatestResponse,
  NormalizedOffersResponse,
  TenderOut,
  TenderCreateRequest,
  TenderInviteOut,
  InviteSentUpdate,
  TermSheet,
  TermSheetsResponse,
  ExtractionStatus,
  PrescreenResponse,
  VerificationField,
  VerificationFieldsResponse,
  VerificationProgress,
  ExtractionSummary,
  AnalysisResponse,
  AnalysisStatusResponse,
  VerdictResponse,
  NarrativeResponse,
  AdminNarrativeResponse,
  TermSheetNarrativeResponse,
  AdminTermSheetNarrativeResponse,
  DealOverviewNarrativeResponse,
  AdminDealOverviewNarrativeResponse,
  CompareReasoningResponse,
  ComparisonResponse,
  VersionComparisonResponse,
  BenchmarksResponse,
  Bank,
  ExtractionMethod,
  NewsResponse,
  NewsCategory,
  NewsCountries,
  SubscriptionStatus,
} from './types';

// Wizard payload types
export type SignupSegment = 'sme' | 'accountant' | 'enterprise';
export type SignupPlan = 'free' | 'pro_monthly' | 'pro_annual';

// Typed error thrown by api.advisor.createDeal when the backend cannot
// resolve an industry (no client.industry, no mappable NACE, no body
// industry). The accountant upload page catches this to render an inline
// industry picker — see AccountantUploadPage.tsx 'needs_industry' phase.
// Backend contract: routers/deals.py:create_deal raises HTTPException 422
// with detail={"code": "industry_required", "message": "..."}.
export class IndustryRequiredError extends Error {
  readonly code = 'industry_required';
  constructor(message: string) {
    super(message);
    this.name = 'IndustryRequiredError';
  }
}

// HANDOFF_ACCOUNTANT_DASHBOARD_PHASE_A — advisor portfolio shapes mirror
// schemas/client.py. Declared inline (not imported from features/accountant/types)
// to avoid a feature → api → feature circular import.
export interface AdvisorPortfolioStats {
  total_clients: number;
  needs_attention: number;
  total_exposure_eur: number;
  // Phase G — count of ACTIVE deals in currencies other than EUR. Drives
  // the StatsRow Total-exposure disclaimer so non-EUR deals can't silently
  // vanish from the KPI.
  non_eur_deal_count: number;
  avg_score: number | null;
  scored_deals_count: number;
  // Phase J — Config E dashboard cards. All four surfaces (Savings,
  // Worth-calling, Renewal, Floating exposure) derive from these 7 fields.
  worth_calling_count: number;
  savings_identified_eur: number;
  next_renewal_days: number | null;
  next_renewal_client_name: string | null;
  next_renewal_amount: number | null;
  floating_exposure_eur: number;
  euribor_3m: number | null;
  // Phase K — Haiku-generated weekly brief. Null until the Sunday Celery
  // beat runs (or when the CDO floor is not met for this advisor's week).
  ai_brief: string | null;
}

// Phase J — one row in the RenegotiationDrawer (clients worth calling).
// Backed by GET /advisor/renegotiation-candidates. Mirrors
// schemas/client.py:RenegotiationCandidate.
export interface RenegotiationCandidate {
  client_id: number;
  client_name: string;
  deal_id: number;
  deal_name: string;
  bank_name: string | null;
  industry: string | null;
  financing_amount: number | null;
  all_in_rate_pct: number | null;     // current all-in rate as percentage
  market_p50_bps: number | null;       // cohort median in basis points
  delta_bps: number | null;            // positive only — bps above the median
  est_annual_saving_eur: number | null;  // positive only
  margin_percentile: number | null;
  // Phase K — drawer state + age.
  // action_state: None | 'navigated' (still Worth calling) | 'contacted'
  //               (Contacted this week) | 'in_review' (In review section).
  // deal_age_days: days since AnalysisResult.created_at — > 90 by the
  //                D5 server-side filter, so always set when the row reaches us.
  action_state: string | null;
  deal_age_days: number | null;
}

export interface AdvisorClient {
  id: number;
  advisor_id: number;
  company_name: string;
  nace_code: string | null;
  vat_number: string | null;  // Phase C.1 — plain-text VAT, CBE/KBO lookup later
  industry: string | null;    // Phase C.2.1 — one of INDUSTRY_CANONICAL or null
  created_at: string;
  updated_at: string;
}

// HANDOFF_ACCOUNTANT_DASHBOARD_PHASE_C2 — bulk VAT import shapes.
// Mirror schemas/client.py {BulkLookupRow, BulkLookupResponse, BulkCreateResponse}.
// Declared inline (not imported from features/) per Phase B dual-types convention.
export type BulkLookupStatus =
  | 'ready'
  | 'already_exists'
  | 'not_in_kbo'
  | 'invalid_format'
  | 'duplicate_in_batch';

export interface BulkLookupRow {
  raw_input: string;
  canonical: string | null;
  company_name: string | null;
  nace_code: string | null;
  juridical_form: string | null;
  industry: string | null;   // Phase C.2.1 — derived from NACE; null if unmapped
  kbo_found: boolean;
  status: BulkLookupStatus;
}

export interface BulkLookupResponse {
  rows: BulkLookupRow[];
  ready_count: number;
  skipped_count: number;
}

// Phase C.2.1 / Phase L — per-row overrides for bulk-create.
// `nace_code` removed (Phase L D1): the backend gets NACE from KBO internally
// at bulk-lookup time and stores it on the Client row; the user never types
// or sees a NACE code as input. `industry` is the editable override.
export interface BulkCreateRow {
  vat_number: string;
  company_name?: string | null;
  industry?: string | null;
}

export interface BulkCreateResponse {
  created: number;
  skipped: number;
}

// HANDOFF_ACCOUNTANT_DASHBOARD_PHASE_B — AttentionQueue + ClientPortfolioTable
// data shapes. Mirror schemas/client.py FlaggedDealResponse + ClientWithStatsResponse.
export interface FlaggedDeal {
  deal_id: number;
  deal_name: string;
  client_id: number;
  client_name: string;
  score: number | null;
  flag_reason: 'critical' | 'below_threshold' | 'awaiting_analysis';
  flag_priority: number;
  days_since_upload: number;
  // Phase D — latest TermSheet id for this deal; null only in degenerate states.
  // Drives /portal/deals/:termSheetId routing from AttentionQueue rows.
  term_sheet_id: number | null;
}

export interface ClientWithStats {
  id: number;
  company_name: string;
  // Phase L — `nace_code` replaced by `industry` (D1). Canonical industry
  // label is the only user-visible classifier on the portfolio table.
  industry: string | null;
  deal_count: number;
  avg_score: number | null;
  total_exposure_eur: number;
  last_activity_days: number | null;
  has_flagged_deal: boolean;
}

// HANDOFF_ACCOUNTANT_DASHBOARD_PHASE_C — POST /deals/{id}/term-sheets response
// shape. Mirrors schemas/term_sheets.py:TermSheetUploadResponse, narrowed to the
// fields the accountant upload flow consumes.
export interface AdvisorTermSheetUploadResponse {
  id: number;
  deal_id: number;
  status: string;
}

// HANDOFF_ACCOUNTANT_DASHBOARD_PHASE_E — Client Profile page shapes. Mirrors
// schemas/client.py {ClientDetailResponse, ClientDealRow, ClientUpdate}.
// Declared inline (not imported from features/accountant/types) to match the
// Phase B dual-types convention and avoid circular imports.
export interface AdvisorClientDealRow {
  deal_id: number;
  term_sheet_id: number | null;
  deal_name: string;
  financing_purpose: string | null;
  financing_amount: number | null;
  currency: string | null;
  score: number | null;
  flag_reason: 'critical' | 'below_threshold' | 'awaiting_analysis' | null;
  created_at: string;
}

export interface AdvisorClientDetail {
  id: number;
  company_name: string;
  nace_code: string | null;     // kept — read-only secondary on the profile header (Phase L D3)
  industry: string | null;      // Phase L — primary user-visible label (D3)
  vat_number: string | null;
  is_archived: boolean;
  created_at: string;
  total_deals: number;
  avg_score: number | null;
  total_exposure_eur: number;
  deals: AdvisorClientDealRow[];
}

export interface AdvisorClientUpdate {
  company_name?: string;
  vat_number?: string;
  // Phase L — `nace_code` removed (D1). Accountants edit industry only;
  // NACE is set at create time from the KBO lookup and never user-editable.
  industry?: string | null;
}

/**
 * Full signup-wizard payload appended to /register when the user completes
 * the multi-step wizard (vs. the simple email/password flow). All fields are
 * optional server-side (backend `UserRegister` pydantic model), but callers
 * should send everything the user provided.
 */
export interface WizardRegistrationPayload {
  full_name?: string;
  signup_segment?: SignupSegment;
  company_name?: string;
  firm_name?: string;
  industry?: string;
  job_title?: string;
  client_count?: number;
  vat_number?: string;
  data_sharing_consent?: boolean;
  benchmark_consent?: boolean;
  marketing_consent?: boolean;
  selected_plan?: SignupPlan;
  revenue_range?: string;
  employees_range?: string;
  years_operating?: string;
}

/**
 * Full hosted-checkout payload sent by the unauthenticated signup branch.
 * Matches backend `CreateHostedCheckoutRequest` (aliased to camelCase).
 */
export interface HostedCheckoutSignupPayload {
  email: string;
  fullName?: string;
  password: string;
  signupSegment?: SignupSegment;
  companyName?: string;
  vatNumber?: string;
  firmName?: string;
  clientCount?: number;
  selectedTier: 'pro_monthly' | 'pro_annual';
  vi26Confirmed: boolean;
  locale: 'en' | 'nl' | 'fr';
}

// Token management
const TOKEN_KEY = 'credia_token';

function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

// Create axios instance
const client: AxiosInstance = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle token refresh
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

async function refreshToken(): Promise<string> {
  const token = getStoredToken();
  if (!token) {throw new Error('No token to refresh');}

  const response = await axios.post<AuthResponse>('/refresh', null, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const newToken = response.data.access_token;
  setStoredToken(newToken);
  return newToken;
}

client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Handle 401 - attempt token refresh
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest.url?.includes('/login') &&
      !originalRequest.url?.includes('/refresh')
    ) {
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshToken()
          .finally(() => {
            isRefreshing = false;
            refreshPromise = null;
          });
      }

      try {
        const newToken = await refreshPromise;
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return client(originalRequest);
      } catch {
        clearStoredToken();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    // Handle 402/403 — subscription or tier restriction → redirect to Stripe Hosted Checkout.
    // The request is authenticated (JWT auto-added by the request interceptor),
    // so the backend takes the "upgrade" branch of /billing/create-checkout-session.
    if (error.response?.status === 402 || error.response?.status === 403) {
      const detail = typeof error.response?.data === 'object'
        ? (error.response.data as Record<string, unknown>)?.detail
        : '';
      const detailStr = typeof detail === 'string' ? detail : '';
      if (
        error.response?.status === 402 ||
        /limit|tier|upgrade|subscription/i.test(detailStr)
      ) {
        window.dispatchEvent(new CustomEvent('subscription-required', {
          detail: error.response.data,
        }));
        // Fire-and-forget: kick off hosted checkout then redirect.
        // If it fails (e.g. network hiccup), the error still propagates to the caller.
        client
          .post<{ checkoutUrl: string; sessionId: string }>(
            '/billing/create-checkout-session',
            { selectedTier: 'pro_monthly', locale: 'en', vi26Confirmed: true },
          )
          .then((r) => {
            safeExternalRedirect(r.data.checkoutUrl);
          })
          .catch(() => {
            // The upstream error still rejects below; surface the auto-checkout
            // failure so the user isn't left with a silently dead action.
            useUIStore.getState().showToast('Could not start checkout. Try again.', 'error');
          });
      }
    }

    return Promise.reject(error);
  }
);

// ============ API Methods ============

export const api = {
  // Token management
  token: {
    get: getStoredToken,
    set: setStoredToken,
    clear: clearStoredToken,
    getExpiry: (token: string) => getTokenExpiry(token),
    isExpired: () => {
      const token = getStoredToken();
      if (!token) {return true;}
      const expiry = getTokenExpiry(token);
      if (!expiry) {return true;}
      return Date.now() > expiry;
    },
    isExpiringSoon: (bufferMs = 5 * 60 * 1000) => {
      const token = getStoredToken();
      if (!token) {return true;}
      const expiry = getTokenExpiry(token);
      if (!expiry) {return true;}
      return Date.now() > expiry - bufferMs;
    },
  },

  // Auth
  auth: {
    getUsage: async (): Promise<AccountUsageResponse> => {
      const response = await client.get<AccountUsageResponse>('/auth/usage');
      return response.data;
    },

    login: async (email: string, password: string): Promise<AuthResponse> => {
      const response = await client.post<AuthResponse>('/login', { email, password });
      if (response.data.access_token) {
        setStoredToken(response.data.access_token);
      }
      return response.data;
    },

    googleLogin: async (credential: string): Promise<AuthResponse> => {
      const response = await client.post<AuthResponse>('/auth/google', { credential });
      if (response.data.access_token) {
        setStoredToken(response.data.access_token);
      }
      return response.data;
    },

    register: async (
      email: string,
      password: string,
      accepted_terms: boolean = true,
      cookie_consent: boolean = false,
      wizardData?: WizardRegistrationPayload,
      /**
       * When 'wizard', the backend mints an access_token and returns it in
       * the response body (HANDOFF_WIZARD_JWT_MINT). Callers should then
       * `api.token.set(response.access_token)` to authenticate subsequent
       * PATCH /me calls. For any non-wizard caller, omit this — the
       * response will not include an access_token and the user still needs
       * to verify their email + /login.
       */
      signup_context?: 'wizard' | 'legacy',
    ): Promise<{
      message?: string;
      email: string;
      email_verified: boolean;
      redirect?: string | null;
      access_token?: string;
      token_type?: string;
      user?: User;
    }> => {
      const response = await client.post<{
        message?: string;
        email: string;
        email_verified: boolean;
        redirect?: string | null;
        access_token?: string;
        token_type?: string;
        user?: User;
      }>('/register', {
        email,
        password,
        accepted_terms,
        cookie_consent,
        ...(wizardData ?? {}),
        ...(signup_context ? { signup_context } : {}),
      });
      return response.data;
    },

    forgotPassword: async (email: string): Promise<{ message: string }> => {
      const response = await client.post<{ message: string }>('/forgot-password', { email });
      return response.data;
    },

    resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
      const response = await client.post<{ message: string }>('/reset-password', {
        token,
        new_password: newPassword,
      });
      return response.data;
    },

    verifyEmail: async (token: string): Promise<{ message: string }> => {
      const response = await client.post<{ message: string }>('/verify-email', { token });
      return response.data;
    },

    resendVerification: async (email: string): Promise<{ message: string }> => {
      const response = await client.post<{ message: string }>('/resend-verification', { email });
      return response.data;
    },

    updateProfile: async (data: ProfileUpdate): Promise<User> => {
      // Forward the full ProfileUpdate payload. Cherry-picking fields here
      // silently dropped wizard-step fields like `signup_segment`,
      // `full_name`, `firm_name`, `vat_number`, etc. — the PATCH would
      // succeed with an empty body and the backend would never store the
      // segment, leaving the user stuck at SignupCompleteRoute (step 2
      // redirect) when they later clicked through to /dashboard. Backend
      // validates its own schema, so any unknown keys are ignored server-side.
      const response = await client.patch<User>('/me', data);
      return response.data;
    },

    logout: () => {
      clearStoredToken();
      sessionStorage.removeItem('currentDealId');
      sessionStorage.removeItem('currentTermSheetId');
      window.location.href = '/login';
    },

    getMe: async (): Promise<User> => {
      const response = await client.get<User>('/me');
      return response.data;
    },

    refresh: async (): Promise<string> => {
      return refreshToken();
    },

    changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
      await client.patch('/me/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
    },

    deleteAccount: async (password: string): Promise<void> => {
      await client.delete('/me', {
        data: { password },
      });
    },
  },

  // Deals
  deals: {
    list: async (includeArchived = false): Promise<DealsResponse> => {
      const url = includeArchived ? '/deals?include_archived=true' : '/deals';
      const response = await client.get<DealsResponse>(url);
      return response.data;
    },

    get: async (dealId: number): Promise<Deal> => {
      const response = await client.get<Deal>(`/deals/${dealId}`);
      return response.data;
    },

    create: async (data: DealCreate): Promise<Deal> => {
      const response = await client.post<Deal>('/deals', data);
      return response.data;
    },

    update: async (dealId: number, data: DealUpdate): Promise<Deal> => {
      const response = await client.put<Deal>(`/deals/${dealId}`, data);
      return response.data;
    },

    delete: async (dealId: number): Promise<void> => {
      await client.delete(`/deals/${dealId}`);
    },

    archive: async (dealId: number): Promise<Deal> => {
      const response = await client.post<Deal>(`/deals/${dealId}/archive`);
      return response.data;
    },

    unarchive: async (dealId: number): Promise<Deal> => {
      const response = await client.post<Deal>(`/deals/${dealId}/unarchive`);
      return response.data;
    },

    reorder: async (dealIds: number[]): Promise<void> => {
      await client.post('/deals/reorder', { deal_ids: dealIds });
    },
  },

  // Term Sheets
  termSheets: {
    list: async (dealId: number): Promise<TermSheetsResponse> => {
      const response = await client.get<RawWire>(`/deals/${dealId}/term-sheets`);
      // Backend returns a flat array with different field names — normalize
      const raw: RawWire[] = Array.isArray(response.data) ? response.data : (response.data?.term_sheets || []);
      const term_sheets: TermSheet[] = raw.map((ts: RawWire) => ({
        id: ts.id,
        deal_id: ts.deal_id ?? dealId,
        filename: ts.original_filename ?? ts.filename ?? 'Unknown',
        file_size: ts.file_size_bytes ?? ts.file_size ?? 0,
        status: ts.status,
        bank_id: ts.bank_id,
        bank_name: ts.bank_name,
        other_bank_name: ts.other_bank_name,
        extraction_method: ts.extraction_method,
        overall_confidence: ts.average_confidence ?? ts.overall_confidence,
        version_number: ts.version_number,
        is_current_offer: ts.is_current_offer,
        created_at: ts.uploaded_at ?? ts.created_at ?? '',
        updated_at: ts.extraction_completed_at ?? ts.uploaded_at ?? ts.updated_at ?? '',
        // HANDOFF_COMPARE_CARDS_REDESIGN — per-sheet scoring + metric
        // summary for the unified CompareCard. Backend populates these
        // once extraction + scoring complete; null otherwise.
        overall_score: ts.overall_score ?? null,
        extracted_summary: ts.extracted_summary ?? null,
      }));
      return { term_sheets, total: term_sheets.length };
    },

    get: async (termSheetId: number): Promise<TermSheet> => {
      const response = await client.get<TermSheet>(`/term-sheets/${termSheetId}`);
      return response.data;
    },

    upload: async (
      dealId: number,
      file: File,
      options?: {
        bankId?: number;
        otherBankName?: string;
        extractionMethod?: string;
        /** The Ask return leg: route this upload to a tender invite slot.
         *  The server binds the bank from the invite (no bankId sent). */
        tenderInviteId?: number;
        /** Real upload-byte progress (0–100), reported as bytes are sent. */
        onUploadProgress?: (pct: number) => void;
      }
    ): Promise<TermSheet> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('deal_id', String(dealId));
      if (options?.bankId) {formData.append('bank_id', String(options.bankId));}
      if (options?.otherBankName) {formData.append('other_bank_name', options.otherBankName);}
      if (options?.extractionMethod) {formData.append('extraction_method', options.extractionMethod);}
      if (options?.tenderInviteId) {formData.append('tender_invite_id', String(options.tenderInviteId));}

      const onUploadProgress = options?.onUploadProgress;
      const response = await client.post<TermSheet>(
        `/deals/${dealId}/term-sheets`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: onUploadProgress
            ? (e) => {
                if (e.total) {onUploadProgress(Math.round((e.loaded / e.total) * 100));}
              }
            : undefined,
        }
      );
      return response.data;
    },

    getExtractionStatus: async (termSheetId: number): Promise<ExtractionStatus> => {
      const response = await client.get<ExtractionStatus>(
        `/term-sheets/${termSheetId}/extraction-status`
      );
      return response.data;
    },

    getPrescreen: async (termSheetId: number): Promise<PrescreenResponse> => {
      const response = await client.get<PrescreenResponse>(
        `/term-sheets/${termSheetId}/prescreen`
      );
      return response.data;
    },

    reprocess: async (termSheetId: number, force = false): Promise<void> => {
      await client.post(`/term-sheets/${termSheetId}/reprocess?force=${force}`);
    },

    delete: async (termSheetId: number): Promise<void> => {
      await client.delete(`/term-sheets/${termSheetId}`);
    },

    download: async (termSheetId: number): Promise<void> => {
      // Authenticated blob download — the /download endpoint requires JWT
      // (Depends(get_current_user)), so we cannot use window.open(). Match
      // the exportData pattern: fetch via the axios client (which auto-
      // injects the Authorization header), wrap in an object URL, and
      // synthesize an <a download> click.
      const response = await client.get(`/term-sheets/${termSheetId}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `term-sheet-${termSheetId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },

    compareVersions: async (termSheetId: number, previousId: number): Promise<VersionComparisonResponse> => {
      const response = await client.get<VersionComparisonResponse>(
        `/term-sheets/${termSheetId}/compare-version/${previousId}`
      );
      return response.data;
    },

    // Citations Phase 1 — mint a fresh signed URL for the PDF on demand.
    // Used by the "View term sheet" link in ChatMessage. Backend returns
    // 410 when the source file has been deleted post-analysis under the
    // user's GDPR retention preference.
    getDownloadUrl: async (
      termSheetId: number,
    ): Promise<{ download_url: string; expires_in: number }> => {
      const response = await client.get<{ download_url: string; expires_in: number }>(
        `/term-sheets/${termSheetId}/download-url`,
      );
      return response.data;
    },
  },

  // Verification
  verification: {
    getFields: async (
      termSheetId: number,
      options?: {
        includeVerified?: boolean;
        confidenceFilter?: 'high' | 'medium' | 'low';
        categoryFilter?: string;
      }
    ): Promise<VerificationFieldsResponse> => {
      const params = new URLSearchParams();
      if (options?.includeVerified) {params.append('include_verified', 'true');}
      if (options?.confidenceFilter) {params.append('confidence_filter', options.confidenceFilter);}
      if (options?.categoryFilter) {params.append('category_filter', options.categoryFilter);}

      const queryString = params.toString();
      const url = `/term-sheets/${termSheetId}/verification-fields${queryString ? '?' + queryString : ''}`;
      const response = await client.get<VerificationFieldsResponse>(url);
      return response.data;
    },

    getFieldEvidence: async (termSheetId: number, fieldName: string): Promise<VerificationField> => {
      const response = await client.get<VerificationField>(
        `/term-sheets/${termSheetId}/fields/${fieldName}/evidence`
      );
      return response.data;
    },

    verifyField: async (
      termSheetId: number,
      fieldName: string,
      data: {
        action: 'approve' | 'correct' | 'flag';
        correctedValue?: unknown;
        correctionReason?: string;
      }
    ): Promise<VerificationField> => {
      const response = await client.patch<VerificationField>(
        `/term-sheets/${termSheetId}/fields/${fieldName}/verify`,
        {
          action: data.action,
          corrected_value: data.correctedValue,
          correction_reason: data.correctionReason,
        }
      );
      return response.data;
    },

    bulkVerify: async (
      termSheetId: number,
      fieldNames: string[],
      action: 'approve' | 'flag'
    ): Promise<void> => {
      await client.post(`/term-sheets/${termSheetId}/bulk-verify`, {
        field_names: fieldNames,
        action,
      });
    },

    getProgress: async (termSheetId: number): Promise<VerificationProgress> => {
      const response = await client.get<VerificationProgress>(
        `/term-sheets/${termSheetId}/verification-progress`
      );
      return response.data;
    },

    getSummary: async (termSheetId: number): Promise<ExtractionSummary> => {
      const response = await client.get<RawWire>(
        `/term-sheets/${termSheetId}/extraction-summary`
      );
      const raw = response.data;
      // Normalize backend field names to frontend ExtractionSummary type
      const byConf = raw.by_confidence || raw.confidence_breakdown || {};
      const totalFields = raw.field_count ?? raw.total_fields ?? 0;
      const highCount = byConf.high ?? 0;
      const medCount = byConf.medium ?? 0;
      const lowCount = byConf.low ?? 0;
      const extractedFields = raw.extracted_fields ?? (highCount + medCount + lowCount);
      return {
        term_sheet_id: raw.term_sheet_id ?? termSheetId,
        extraction_method: raw.extraction_method ?? raw.extraction_status ?? '',
        total_fields: totalFields,
        extracted_fields: extractedFields,
        coverage_pct: raw.coverage_pct ?? (totalFields > 0 ? (extractedFields / totalFields) * 100 : 0),
        confidence_breakdown: {
          high: highCount,
          medium: medCount,
          low: lowCount,
        },
        processing_time_seconds: raw.processing_time_seconds ?? raw.total_seconds ?? 0,
      };
    },
  },

  // Analysis
  analysis: {
    get: async (dealId: number, termSheetId?: number): Promise<AnalysisResponse> => {
      const url = termSheetId
        ? `/deals/${dealId}/analysis?term_sheet_id=${termSheetId}`
        : `/deals/${dealId}/analysis`;
      const response = await client.get<AnalysisResponse>(url);
      return response.data;
    },
    /**
     * Phase 0 T-002: Poll async scoring status.
     *
     * Returns { status: 'pending'|'running'|'ready'|'error', ... }.
     * Legacy rows with NULL analysis_status are promoted to 'ready'
     * server-side, so the dashboard never gets stuck polling old data.
     */
    getStatus: async (
      dealId: number,
      termSheetId?: number
    ): Promise<AnalysisStatusResponse> => {
      const url = termSheetId
        ? `/deals/${dealId}/analysis/status?term_sheet_id=${termSheetId}`
        : `/deals/${dealId}/analysis/status`;
      const response = await client.get<AnalysisStatusResponse>(url);
      return response.data;
    },
  },

  // Phase 1 T-105: Deterministic verdict — dashboard VerdictCard.
  verdict: {
    get: async (dealId: number): Promise<VerdictResponse> => {
      const response = await client.get<VerdictResponse>(`/deals/${dealId}/verdict`);
      return response.data;
    },
  },

  // HANDOFF_DEAL_NARRATIVE_V1: LLM copy for the active deal card hero.
  narrative: {
    get: async (dealId: number): Promise<NarrativeResponse> => {
      const response = await client.get<NarrativeResponse>(`/deals/${dealId}/narrative`);
      return response.data;
    },
    /**
     * Admin read with full provenance — drives the Extraction Inspector
     * Narrative tab. Returns 403 for non-admins (handled upstream).
     */
    getAdmin: async (dealId: number): Promise<AdminNarrativeResponse> => {
      const response = await client.get<AdminNarrativeResponse>(
        `/admin/deals/${dealId}/narrative`,
      );
      return response.data;
    },
    /** Admin synchronous regenerate — returns the freshly stored row. */
    regenerate: async (dealId: number): Promise<AdminNarrativeResponse> => {
      const response = await client.post<AdminNarrativeResponse>(
        `/admin/deals/${dealId}/narrative/regenerate`,
      );
      return response.data;
    },
  },

  // HANDOFF_DEAL_OVERVIEW_NARRATIVE_V1: cross-bank synthesis on the deal
  // intelligence card. 404 while the Celery task is still running, or
  // when the deal has fewer than 2 ANALYZED term sheets.
  dealOverviewNarrative: {
    get: async (dealId: number): Promise<DealOverviewNarrativeResponse> => {
      const response = await client.get<DealOverviewNarrativeResponse>(
        `/deals/${dealId}/overview-narrative`,
      );
      return response.data;
    },
    /** Admin read with full provenance — drives the inspector tab. */
    getAdmin: async (
      dealId: number,
    ): Promise<AdminDealOverviewNarrativeResponse> => {
      const response = await client.get<AdminDealOverviewNarrativeResponse>(
        `/admin/deals/${dealId}/overview-narrative`,
      );
      return response.data;
    },
    /** Admin synchronous regenerate. */
    regenerate: async (
      dealId: number,
    ): Promise<AdminDealOverviewNarrativeResponse> => {
      const response = await client.post<AdminDealOverviewNarrativeResponse>(
        `/admin/deals/${dealId}/overview-narrative/regenerate`,
      );
      return response.data;
    },
  },

  // COMPARE_REASONING_V1: profile-aware verdict narrative for the /compare
  // view. Keyed (deal_id, profile) — the lens shapes ORDER and EMPHASIS, never
  // the facts. 404 while the narrative has not been generated yet.
  compareReasoning: {
    get: async (
      dealId: number,
      profile: string,
    ): Promise<CompareReasoningResponse> => {
      const { data } = await client.get<CompareReasoningResponse>(
        `/deals/${dealId}/compare-reasoning`,
        { params: { profile } },
      );
      return data;
    },
  },

  // HANDOFF_TERM_SHEET_NARRATIVE_V1: LLM reasoning for the analysis page.
  termSheetNarrative: {
    get: async (termSheetId: number): Promise<TermSheetNarrativeResponse> => {
      const response = await client.get<TermSheetNarrativeResponse>(
        `/term-sheets/${termSheetId}/narrative`,
      );
      return response.data;
    },
    /**
     * Admin read with full provenance — drives the Extraction Inspector
     * Narrative tab's Tier A panel.
     */
    getAdmin: async (
      termSheetId: number,
    ): Promise<AdminTermSheetNarrativeResponse> => {
      const response = await client.get<AdminTermSheetNarrativeResponse>(
        `/admin/term-sheets/${termSheetId}/narrative`,
      );
      return response.data;
    },
    /** Admin synchronous regenerate — returns the freshly stored row. */
    regenerate: async (
      termSheetId: number,
    ): Promise<AdminTermSheetNarrativeResponse> => {
      const response = await client.post<AdminTermSheetNarrativeResponse>(
        `/admin/term-sheets/${termSheetId}/narrative/regenerate`,
      );
      return response.data;
    },
  },

  // Comparison
  comparison: {
    get: async (dealId: number, profile?: string | null): Promise<ComparisonResponse> => {
      const response = await client.get<RawWire>(`/deals/${dealId}/compare`, {
        params: profile ? { profile } : undefined,
      });
      const raw = response.data;

      // Backend returns { banks: [{ bank_name, current_term_sheet: { margin_bps, ... } }] }
      // Frontend expects { term_sheets: [{ id, bank_name, margin_bps, ... }] }
      const banks = raw.banks || [];
      const term_sheets = banks.map((bank: RawWire) => {
        const ts = bank.current_term_sheet || {};
        return {
          id: ts.term_sheet_id,
          bank_name: bank.bank_name || ts.bank_name || 'Unknown Bank',
          margin_bps: ts.margin_bps,
          all_in_rate_pct: ts.all_in_rate_pct,
          arrangement_fee_pct: ts.arrangement_fee_pct,
          commitment_fee_pct: ts.commitment_fee_pct,
          commitment_fee_bps: ts.commitment_fee_bps,
          max_leverage_ratio: ts.max_leverage_ratio,
          min_interest_coverage_ratio: ts.min_interest_coverage_ratio,
          min_dscr: ts.min_dscr,
          facility_amount: ts.facility_amount,
          maturity_years: ts.maturity_years,
          tenor_months: ts.tenor_months,
          grace_period_months: ts.grace_period_months,
          repayment_type: ts.repayment_type,
          rate_type: ts.rate_type,
          prepayment_allowed: ts.prepayment_allowed,
          status: ts.status,
          margin_percentile: ts.margin_percentile,
          // Structured arrays
          financial_covenants: ts.financial_covenants,
          security_types: ts.security_types,
          collateral_description: ts.collateral_description,
          // HANDOFF_DASHBOARD_EXTRACTION_FIDELITY Step 8: additional
          // fields so the Compare matrix can run AnalysisPage's helpers.
          base_rate: ts.base_rate,
          operational_covenants: ts.operational_covenants,
          facilities: ts.facilities,
          total_upfront_fees: ts.total_upfront_fees,
          mortgage_amount: ts.mortgage_amount,
          guarantee_amount: ts.guarantee_amount,
          guarantee_provider: ts.guarantee_provider,
          shares_pledged_percentage: ts.shares_pledged_percentage,
          key_man_insurance_required: ts.key_man_insurance_required,
          property_insurance_required: ts.property_insurance_required,
          // Scoring
          overall_score: ts.overall_score,
          margin_score: ts.margin_score,
          fee_score: ts.fee_score,
          structure_score: ts.structure_score,
          conditions_score: ts.conditions_score,
          collateral_score: ts.collateral_score,
          // COMPARE_VIEW_HANDOFF v4 — profile-aware composite (server-computed
          // under active_profile). overall_score above = market-standard (dual
          // display); floor_warning surfaces a component <40 the profile can't mask.
          profile_composite: ts.profile_composite,
          profile_used: ts.profile_used,
          weights_applied: ts.weights_applied,
          floor_warning: ts.floor_warning,
          // COMPARE_VIEW_BUILD_HANDOFF §1 — all-in € basis (landing-computed,
          // tie-inclusive when RANKING_V21 on) + load-bearing absence signals.
          // Carried verbatim; null with the flag off → empty states render.
          total_cost_with_fees: ts.total_cost_with_fees,
          effective_rate: ts.effective_rate,
          repayment_schedule: ts.repayment_schedule,
          tied_products: ts.tied_products,
          covenant_section_status: ts.covenant_section_status,
          covenant_section_quote: ts.covenant_section_quote,
          section_coverage: ts.section_coverage,
          explicit_absences: ts.explicit_absences,
          repayment_frequency: ts.repayment_frequency,
        };
      });

      return {
        deal_id: raw.deal_id,
        deal_name: raw.deal_name,
        term_sheets,
        field_comparison: raw.field_comparison || {},
        // Pass through extra backend fields for richer UI
        recommendation_text: raw.recommendation_text,
        best_margin_bank_id: raw.best_margin_bank_id,
        best_fee_bank_id: raw.best_fee_bank_id,
        best_overall_bank_id: raw.best_overall_bank_id,
        financing_amount: raw.financing_amount,
        currency: raw.currency,
        industry: raw.industry,
        active_profile: raw.active_profile,
      } as ComparisonResponse;
    },
  },

  // Benchmarks
  benchmarks: {
    get: async (params?: Record<string, string>): Promise<BenchmarksResponse> => {
      const queryString = params ? new URLSearchParams(params).toString() : '';
      const url = `/benchmarks${queryString ? '?' + queryString : ''}`;
      const response = await client.get<BenchmarksResponse>(url);
      return response.data;
    },

    getMarketRates: async () => {
      const response = await client.get('/market-rates');
      return response.data;
    },

    // ECB MIR — Belgian new-business lending averages by size band x fixation
    // period. Free-tier data (the Ask briefing's market block); 24h server cache.
    getMirRates: async (): Promise<MirRatesResponse> => {
      const response = await client.get<MirRatesResponse>('/mir-rates');
      return response.data;
    },
  },

  // The Ask (Phase 1 Block D) — deterministic; server rebuilds every fact
  // from the Deal row, the client sends ONLY stances + scheme.
  ask: {
    get: async (dealId: number): Promise<AskLatestResponse> => {
      const response = await client.get<AskLatestResponse>(`/deals/${dealId}/ask`);
      return response.data;
    },
    preview: async (dealId: number): Promise<AskPreviewResponse> => {
      const response = await client.get<AskPreviewResponse>(`/deals/${dealId}/ask/preview`);
      return response.data;
    },
    create: async (dealId: number, body: AskLockRequest): Promise<AskCreateResponse> => {
      const response = await client.post<AskCreateResponse>(`/deals/${dealId}/ask`, body);
      return response.data;
    },
  },

  // The Ask Phase 2 — tender (package + send). Server rebuilds every fact;
  // the client sends only bank ids + language + attestation booleans.
  tender: {
    // Correctly-typed bank list (GET /banks) — the `Bank` type used elsewhere
    // doesn't match the wire; this one does.
    bankOptions: async (): Promise<BankListItem[]> =>
      (await client.get<BankListItem[]>('/banks')).data,
    get: async (dealId: number): Promise<TenderLatestResponse> =>
      (await client.get<TenderLatestResponse>(`/deals/${dealId}/tender`)).data,
    // Phase 4 tender-compare — the ONLY euro-exposing surface (PRO-gated server-side;
    // a FREE user's request 402/403s and the global interceptor handles it).
    normalizedOffers: async (dealId: number): Promise<NormalizedOffersResponse> =>
      (await client.get<NormalizedOffersResponse>(`/deals/${dealId}/tender/normalized-offers`)).data,
    create: async (dealId: number, body: TenderCreateRequest): Promise<TenderOut> =>
      (await client.post<TenderOut>(`/deals/${dealId}/tender`, body)).data,
    updateLanguage: async (tenderId: number, language: 'en' | 'nl' | 'fr'): Promise<TenderOut> =>
      (await client.patch<TenderOut>(`/tenders/${tenderId}`, { package_language: language })).data,
    // Exactly one of bankId / otherBankName (the server enforces the XOR).
    addInvite: async (
      tenderId: number,
      invite: { bankId?: number; otherBankName?: string },
    ): Promise<TenderInviteOut> =>
      (
        await client.post<TenderInviteOut>(`/tenders/${tenderId}/invites`, {
          bank_id: invite.bankId,
          other_bank_name: invite.otherBankName,
        })
      ).data,
    removeInvite: async (inviteId: number): Promise<void> => {
      await client.delete(`/tender-invites/${inviteId}`);
    },
    markSent: async (inviteId: number, body: InviteSentUpdate): Promise<TenderInviteOut> =>
      (await client.patch<TenderInviteOut>(`/tender-invites/${inviteId}/sent`, body)).data,
    // "Mark as no offer" — reversible while the invite has not replied.
    setDeclined: async (inviteId: number, declined: boolean): Promise<TenderInviteOut> =>
      (await client.patch<TenderInviteOut>(`/tender-invites/${inviteId}/declined`, { declined })).data,
  },

  // Advisor / Accountant workspace (HANDOFF_ACCOUNTANT_DASHBOARD_PHASE_A)
  advisor: {
    portfolio: async (): Promise<AdvisorPortfolioStats> => {
      const response = await client.get<AdvisorPortfolioStats>('/advisor/portfolio');
      return response.data;
    },
    // Phase J — lazy-fetched when the RenegotiationDrawer opens.
    renegotiationCandidates: async (): Promise<RenegotiationCandidate[]> => {
      const response = await client.get<RenegotiationCandidate[]>(
        '/advisor/renegotiation-candidates',
      );
      return response.data;
    },
    // Phase K — record a candidate action (navigated / contacted). Idempotent.
    recordCandidateAction: async (body: {
      deal_id: number;
      client_id: number;
      action_type: 'contacted' | 'navigated';
    }): Promise<void> => {
      await client.post('/advisor/candidate-action', body);
    },
    // Phase K — undo a contacted action within the 5s grace window. Always
    // safe to call; the backend treats a missing row as a no-op (204).
    undoCandidateAction: async (
      deal_id: number,
      action_type: 'contacted' | 'navigated' = 'contacted',
    ): Promise<void> => {
      await client.delete('/advisor/candidate-action', {
        params: { deal_id, action_type },
      });
    },
    // Phase K — fire-and-forget telemetry emit. Used by the drawer to signal
    // open / navigate / actioned events. Never await this — drawer UX must
    // not block on telemetry, and a 4xx/5xx must not surface to the user.
    emitTelemetry: (event_type: string, payload: Record<string, unknown>): void => {
      client
        .post('/chat/telemetry', { event_type, payload })
        .catch(() => {
          /* swallow — telemetry failures are not user-facing */
        });
    },
    listClients: async (): Promise<AdvisorClient[]> => {
      const response = await client.get<AdvisorClient[]>('/advisor/clients');
      return response.data;
    },
    // Phase L — `nace_code` removed (D1); replaced by `industry`. NACE is set
    // server-side from the KBO lookup at bulk-lookup time and never user-input.
    createClient: async (body: {
      company_name: string;
      vat_number?: string;            // Phase C.1
      industry?: string | null;       // Phase L — one of INDUSTRY_CANONICAL
    }): Promise<AdvisorClient> => {
      const response = await client.post<AdvisorClient>('/advisor/clients', body);
      return response.data;
    },
    // Phase B — flagged deals queue + per-client aggregated stats.
    flags: async (): Promise<FlaggedDeal[]> => {
      const response = await client.get<FlaggedDeal[]>('/advisor/flags');
      return response.data;
    },
    clientStats: async (): Promise<ClientWithStats[]> => {
      const response = await client.get<ClientWithStats[]>('/advisor/client-stats');
      return response.data;
    },
    // Phase C — create deal scoped to a client and upload a term sheet to it.
    // createDeal hits the generic POST /deals; the new client_id field is
    // ownership-checked server-side in routers/deals.py.
    // uploadTermSheet mirrors api.termSheets.upload — same endpoint, same
    // multipart pattern; exposed under api.advisor.* so the accountant flow
    // stays inside its own typed namespace per the canonical correction rule.
    // Industry: normally derived server-side from Client.industry or NACE.
    // When the client has neither, the backend returns a structured 422 we
    // convert to IndustryRequiredError so the page can render an inline
    // picker and retry with `industry` populated.
    createDeal: async (body: {
      financing_purpose: string;
      financing_amount: number;
      timeline_urgency: string;
      client_id: number | null;
      industry?: string;
    }): Promise<{ id: number; deal_name: string }> => {
      try {
        const response = await client.post<{ id: number; deal_name: string }>('/deals', body);
        return response.data;
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 422) {
          const detail = (err.response.data as { detail?: unknown })?.detail;
          if (
            detail &&
            typeof detail === 'object' &&
            (detail as { code?: unknown }).code === 'industry_required'
          ) {
            const message = typeof (detail as { message?: unknown }).message === 'string'
              ? (detail as { message: string }).message
              : 'Industry is required for this deal.';
            throw new IndustryRequiredError(message);
          }
        }
        throw err;
      }
    },
    uploadTermSheet: async (
      dealId: number,
      file: File,
    ): Promise<AdvisorTermSheetUploadResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await client.post<AdvisorTermSheetUploadResponse>(
        `/deals/${dealId}/term-sheets`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },
    // Phase E — client profile page + edit + archive toggle.
    getClient: async (clientId: number): Promise<AdvisorClientDetail> => {
      const response = await client.get<AdvisorClientDetail>(`/advisor/clients/${clientId}`);
      return response.data;
    },
    updateClient: async (
      clientId: number,
      body: AdvisorClientUpdate,
    ): Promise<AdvisorClient> => {
      const response = await client.patch<AdvisorClient>(`/advisor/clients/${clientId}`, body);
      return response.data;
    },
    archiveClient: async (clientId: number): Promise<AdvisorClient> => {
      const response = await client.patch<AdvisorClient>(`/advisor/clients/${clientId}/archive`, {});
      return response.data;
    },
    // Phase C.2 — bulk VAT import (2-step flow).
    bulkLookup: async (vatNumbers: string[]): Promise<BulkLookupResponse> => {
      const response = await client.post<BulkLookupResponse>(
        '/advisor/clients/bulk-lookup',
        { vat_numbers: vatNumbers },
      );
      return response.data;
    },
    // Phase C.2.1 — payload is now BulkCreateRow[] (vat_number plus optional
    // company_name / nace_code / industry overrides). Backend applies override
    // precedence: row.<field> -> KBO lookup -> fallback (canonical VAT for name).
    bulkCreate: async (rows: BulkCreateRow[]): Promise<BulkCreateResponse> => {
      const response = await client.post<BulkCreateResponse>(
        '/advisor/clients/bulk-create',
        { rows },
      );
      return response.data;
    },
  },

  // Banks
  banks: {
    list: async (): Promise<Bank[]> => {
      const response = await client.get<Bank[]>('/banks');
      return response.data;
    },
    intelligence: async (bankName: string): Promise<unknown> => {
      // GET /banks/{name}/intelligence — qualitative profile for BankIntelCard.
      // Caller (useBankIntelligence) types the response.
      const response = await client.get<unknown>(
        `/banks/${encodeURIComponent(bankName)}/intelligence`,
      );
      return response.data;
    },
  },

  // Extraction Methods
  extractionMethods: {
    list: async (includeDeprecated = true): Promise<{ methods: ExtractionMethod[]; default_method: string }> => {
      const response = await client.get<{ methods: ExtractionMethod[]; default_method: string }>(
        `/extraction-methods?include_deprecated=${includeDeprecated}`
      );
      return response.data;
    },
  },

  // Documents
  documents: {
    list: async () => {
      const response = await client.get('/documents');
      return response.data;
    },
  },

  // News
  news: {
    getArticles: async (params?: {
      country?: string;
      category?: string;
      page?: number;
      page_size?: number;
      q?: string;
    }): Promise<NewsResponse> => {
      const searchParams = new URLSearchParams();
      if (params?.country) {searchParams.append('country', params.country);}
      if (params?.category) {searchParams.append('category', params.category);}
      if (params?.page) {searchParams.append('page', String(params.page));}
      if (params?.page_size) {searchParams.append('page_size', String(params.page_size));}
      if (params?.q) {searchParams.append('q', params.q);}
      const queryString = searchParams.toString();
      const url = `/news${queryString ? '?' + queryString : ''}`;
      const response = await client.get<NewsResponse>(url);
      return response.data;
    },

    getCategories: async (): Promise<{ categories: NewsCategory[] }> => {
      const response = await client.get<{ categories: NewsCategory[] }>('/news/categories');
      return response.data;
    },

    getCountries: async (): Promise<NewsCountries> => {
      const response = await client.get<NewsCountries>('/news/countries');
      return response.data;
    },
  },

  // Billing
  billing: {
    /**
     * Hosted Checkout — in-app upgrade flow.
     * Sends the JWT automatically via the request interceptor, so the backend
     * takes the "upgrade" branch of /billing/create-checkout-session.
     * Returns the Stripe-hosted URL; caller redirects `window.location.href`.
     */
    // Note: the legacy inline `createCheckout` (POST /billing/create-checkout)
    // was removed alongside CheckoutModal. Use createCheckoutSession instead.
    createCheckoutSession: async (
      selectedTier: 'pro_monthly' | 'pro_annual' = 'pro_monthly',
      locale: 'en' | 'nl' | 'fr' = 'en',
      /**
       * When called from the unauthenticated signup wizard (Step 3c), pass the
       * full signup payload — backend takes the signup branch and creates or
       * resumes a pending User. When called from an authenticated SPA context
       * (e.g. 402 interceptor), omit this — backend takes the upgrade branch
       * using the JWT-resolved user.
       */
      signupPayload?: Omit<HostedCheckoutSignupPayload, 'selectedTier' | 'locale' | 'vi26Confirmed'>,
    ): Promise<{ checkoutUrl: string; sessionId: string }> => {
      const body = signupPayload
        ? { ...signupPayload, selectedTier, locale, vi26Confirmed: true }
        : { selectedTier, locale, vi26Confirmed: true };
      const response = await client.post<{ checkoutUrl: string; sessionId: string }>(
        '/billing/create-checkout-session',
        body,
      );
      return response.data;
    },

    /**
     * Confirm a completed Stripe Checkout Session and (for signup flow)
     * receive a JWT to log the user in.
     */
    confirmCheckoutSession: async (
      sessionId: string,
    ): Promise<{
      status: 'paid' | 'processing' | 'failed';
      flow?: 'signup' | 'upgrade';
      access_token?: string;
      user_id?: number;
    }> => {
      const response = await client.post('/billing/confirm-checkout-session', {
        sessionId,
      });
      return response.data;
    },

    getPortalUrl: async (): Promise<{ portal_url: string }> => {
      const response = await client.get<{ portal_url: string }>('/billing/portal');
      return response.data;
    },

    getStatus: async (): Promise<SubscriptionStatus> => {
      const response = await client.get<SubscriptionStatus>('/billing/subscription');
      return response.data;
    },
  },

  // Preferences
  preferences: {
    setTheme: async (theme: string): Promise<void> => {
      await client.patch('/me/preferences/theme', { theme });
    },
  },

  // T-31 generate_export — chat PDF export
  exports: {
    /**
     * Stamp the user's first-export confidentiality acknowledgment.
     * Idempotent server-side; safe to call on every download attempt.
     */
    acknowledge: async (): Promise<{ ok: boolean; acknowledged_at: string }> => {
      const response = await client.post('/users/me/export-acknowledge');
      return response.data;
    },
  },

  // Privacy Center (GDPR Art. 15, 17, 18, 20, 21 + ePrivacy Art. 5(3))
  privacy: {
    updateCookieConsent: async (cookie_consent: boolean): Promise<void> => {
      await client.patch('/me/privacy/cookie-consent', { cookie_consent });
    },

    updateConsent: async (data_sharing_consent: boolean): Promise<void> => {
      await client.patch('/me/privacy/consent', { data_sharing_consent });
    },

    updatePdfRetention: async (delete_after_analysis: boolean): Promise<void> => {
      await client.patch('/me/privacy/pdf-retention', { delete_after_analysis });
    },

    exportData: async (): Promise<void> => {
      const response = await client.get('/me/privacy/export?format=json', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'credia-data-export.json');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },

    restrictProcessing: async (): Promise<void> => {
      await client.post('/me/privacy/restrict');
    },

    updateMarketingConsent: async (marketing_consent: boolean): Promise<User> => {
      const response = await client.patch<User>('/me', { marketing_consent });
      return response.data;
    },
  },

  // VAT Lookup (KBO/NACE verification)
  // Backend is POST /me/vat-lookup with body {vat_number}; returns
  // {vat_number, nace_code, risk_cluster, nace_available, company_name,
  // company_address, vies_available, message}. The endpoint is auth-required,
  // so this only works after Step 1 mints a JWT.
  vat: {
    lookup: async (
      vatNumber: string,
    ): Promise<{
      vat_number: string;
      nace_code: string | null;
      risk_cluster: string;
      nace_available: boolean;
      company_name: string | null;
      company_address: string | null;
      vies_available: boolean;
      message: string;
    }> => {
      const response = await client.post('/me/vat-lookup', { vat_number: vatNumber });
      return response.data;
    },
  },

  // Admin
  admin: {
    // Users
    listUsers: async (): Promise<AdminUser[]> => {
      const response = await client.get<AdminUser[]>('/admin/users');
      return response.data;
    },

    getUser: async (userId: number) => {
      const response = await client.get(`/admin/users/${userId}`);
      return response.data;
    },

    createUser: async (data: {
      email: string;
      password: string;
      company_name: string;
      industry: string;
      user_role?: string;
      subscription_tier?: string;
    }) => {
      const formData = new FormData();
      formData.append('email', data.email);
      formData.append('password', data.password);
      formData.append('company_name', data.company_name);
      formData.append('industry', data.industry);
      if (data.user_role) {formData.append('user_role', data.user_role);}
      if (data.subscription_tier) {formData.append('subscription_tier', data.subscription_tier);}

      const response = await client.post('/admin/users', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },

    updateUser: async (userId: number, data: {
      user_role?: string;
      subscription_tier?: string;
      company_name?: string;
      industry?: string;
      job_title?: string;
      chat_monthly_cap_usd?: string;
      chat_daily_cap_usd?: string;
    }) => {
      const formData = new FormData();
      if (data.user_role) {formData.append('user_role', data.user_role);}
      if (data.subscription_tier) {formData.append('subscription_tier', data.subscription_tier);}
      if (data.company_name) {formData.append('company_name', data.company_name);}
      if (data.industry) {formData.append('industry', data.industry);}
      if (data.job_title) {formData.append('job_title', data.job_title);}
      // Caps: send even an empty string so a blank field CLEARS the override.
      if (data.chat_monthly_cap_usd !== undefined) {
        formData.append('chat_monthly_cap_usd', data.chat_monthly_cap_usd);
      }
      if (data.chat_daily_cap_usd !== undefined) {
        formData.append('chat_daily_cap_usd', data.chat_daily_cap_usd);
      }

      const response = await client.put(`/admin/users/${userId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },

    deleteUser: async (userId: number) => {
      const response = await client.delete(`/admin/users/${userId}`);
      return response.data;
    },

    listSubscriptionTiers: async () => {
      const response = await client.get('/admin/subscription-tiers');
      return response.data;
    },

    // Banks
    listBanks: async (filters?: { status?: string; search?: string; sort_by?: string }) => {
      const params = new URLSearchParams();
      if (filters?.status) {params.append('status', filters.status);}
      if (filters?.search) {params.append('search', filters.search);}
      if (filters?.sort_by) {params.append('sort_by', filters.sort_by);}
      const queryString = params.toString();
      const url = `/admin/banks${queryString ? '?' + queryString : ''}`;
      const response = await client.get(url);
      return response.data;
    },

    // Extractions audit
    listExtractions: async (filters?: { status?: string; search?: string; limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (filters?.status) {params.append('status', filters.status);}
      if (filters?.search) {params.append('search', filters.search);}
      if (filters?.limit) {params.append('limit', String(filters.limit));}
      if (filters?.offset) {params.append('offset', String(filters.offset));}
      const queryString = params.toString();
      const url = `/admin/extractions${queryString ? '?' + queryString : ''}`;
      const response = await client.get(url);
      return response.data;
    },

    getExtractionStats: async () => {
      const response = await client.get('/admin/extractions/stats');
      return response.data;
    },

    // HANDOFF_EXTRACTION_INSPECTOR — full detail dump (all ExtractedData
    // columns + AnalysisResult) for a single term sheet.
    getExtraction: async (termSheetId: number) => {
      const response = await client.get(`/admin/extractions/${termSheetId}`);
      return response.data;
    },

    // HANDOFF_EXTRACTION_INSPECTOR — full pipeline diagnosis: source,
    // extraction, calculations, dashboard render, warnings.
    diagnoseExtraction: async (termSheetId: number) => {
      const response = await client.get(`/admin/extractions/${termSheetId}/diagnose`);
      return response.data;
    },

    // Classification Keywords
    listKeywords: async (filters?: { weight_category?: string; language?: string; search?: string }) => {
      const params = new URLSearchParams();
      if (filters?.weight_category) {params.append('weight_category', filters.weight_category);}
      if (filters?.language) {params.append('language', filters.language);}
      if (filters?.search) {params.append('search', filters.search);}
      const queryString = params.toString();
      const url = `/admin/classification-keywords${queryString ? '?' + queryString : ''}`;
      const response = await client.get(url);
      return response.data;
    },

    createKeyword: async (data: {
      keyword: string;
      weight_category: string;
      language: string;
      description?: string;
    }) => {
      const response = await client.post('/admin/classification-keywords', data);
      return response.data;
    },

    deleteKeyword: async (keywordId: number) => {
      const response = await client.delete(`/admin/classification-keywords/${keywordId}`);
      return response.data;
    },

    // Services Health
    getServicesHealth: async (): Promise<ServicesHealthResponse> => {
      const response = await client.get<ServicesHealthResponse>('/admin/services');
      return response.data;
    },

    // Investor portal tokens (admin)
    listInvestorTokens: async () => {
      const response = await client.get('/admin/investor-portal/tokens');
      return response.data;
    },

    createInvestorToken: async (data: Record<string, unknown>) => {
      const response = await client.post('/admin/investor-portal/tokens', data);
      return response.data;
    },

    updateInvestorToken: async (id: number, data: Record<string, unknown>) => {
      const response = await client.patch(`/admin/investor-portal/tokens/${id}`, data);
      return response.data;
    },

    deleteInvestorToken: async (id: number) => {
      const response = await client.delete(`/admin/investor-portal/tokens/${id}`);
      return response.data;
    },

    // Landing site data (Neon database)
    getLandingDashboard: async () => {
      const response = await client.get('/admin/landing/dashboard');
      return response.data;
    },

    // Token usage (Sprint 02) — extraction + chat tokens, USD via models.json
    getTokenUsage: async (params?: { range?: '7d' | '30d' | '90d'; group_by?: 'day' | 'user' | 'deal' | 'model' }) => {
      const q = new URLSearchParams();
      if (params?.range) {q.append('range', params.range);}
      if (params?.group_by) {q.append('group_by', params.group_by);}
      const qs = q.toString();
      const response = await client.get(`/admin/token-usage${qs ? '?' + qs : ''}`);
      return response.data;
    },

    getLandingQa: async (filters?: { filter?: string; limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      if (filters?.filter) {params.append('filter', filters.filter);}
      if (filters?.limit) {params.append('limit', String(filters.limit));}
      if (filters?.offset) {params.append('offset', String(filters.offset));}
      const qs = params.toString();
      const response = await client.get(`/admin/landing/qa${qs ? '?' + qs : ''}`);
      return response.data;
    },

    getLandingQaDetail: async (analysisId: string) => {
      const response = await client.get(`/admin/landing/qa/detail?analysis_id=${analysisId}`);
      return response.data;
    },

    updateLandingQaSetting: async (key: string, value: string) => {
      const response = await client.post('/admin/landing/qa/settings', { key, value });
      return response.data;
    },

    runLandingQa: async (analysisId: string) => {
      const response = await client.post('/admin/landing/qa/run', { analysis_id: analysisId });
      return response.data;
    },

    // Multi-pass extraction status — proxies to landing's ENABLE_MULTI_PASS_EXTRACTION env var.
    // Returns multi_pass_enabled: null when landing is unreachable or auth misconfigured.
    getLandingMultiPassStatus: async (): Promise<{ multi_pass_enabled: boolean | null; error?: string }> => {
      const response = await client.get('/admin/landing/multi-pass-status');
      return response.data;
    },

    // Chat telemetry (PHASE_E §4.1) — admin analytics dashboard
    getChatTelemetryOverview: async (range: '24h' | '7d' | '30d' = '24h') => {
      const response = await client.get(`/admin/chat-telemetry/overview?range=${range}`);
      return response.data;
    },

    listChatTelemetrySessions: async (params: {
      range?: '24h' | '7d' | '30d';
      user_id?: number;
      limit?: number;
      offset?: number;
    } = {}) => {
      const qs = new URLSearchParams();
      if (params.range) {qs.append('range', params.range);}
      if (params.user_id) {qs.append('user_id', String(params.user_id));}
      if (params.limit) {qs.append('limit', String(params.limit));}
      if (params.offset) {qs.append('offset', String(params.offset));}
      const response = await client.get(`/admin/chat-telemetry/sessions?${qs.toString()}`);
      return response.data;
    },

    listChatTelemetryEvents: async (params: {
      range?: '24h' | '7d' | '30d';
      event_type?: string;
      user_id?: number;
      session_id?: string;
      limit?: number;
      offset?: number;
    } = {}) => {
      const qs = new URLSearchParams();
      if (params.range) {qs.append('range', params.range);}
      if (params.event_type) {qs.append('event_type', params.event_type);}
      if (params.user_id) {qs.append('user_id', String(params.user_id));}
      if (params.session_id) {qs.append('session_id', params.session_id);}
      if (params.limit) {qs.append('limit', String(params.limit));}
      if (params.offset) {qs.append('offset', String(params.offset));}
      const response = await client.get(`/admin/chat-telemetry/events?${qs.toString()}`);
      return response.data;
    },

    getChatTelemetryChipCtr: async (range: '24h' | '7d' | '30d' = '7d') => {
      const response = await client.get(`/admin/chat-telemetry/chip-ctr?range=${range}`);
      return response.data;
    },
  },

  // AI Chat V1.1 Item 4 — Recent Chats CRUD (DR #43, 2026-05-03)
  chat: {
    listSessions: async (limit = 20): Promise<ChatSessionSummary[]> => {
      const response = await client.get<ChatSessionSummary[]>(
        `/chat/sessions?limit=${limit}`
      );
      return response.data;
    },
    getSessionMessages: async (sessionId: string): Promise<ChatMessageRow[]> => {
      const response = await client.get<ChatMessageRow[]>(
        `/chat/session/${sessionId}/messages`
      );
      return response.data;
    },
    renameSession: async (sessionId: string, title: string) => {
      const response = await client.patch<{ id: string; title: string }>(
        `/chat/session/${sessionId}/title`,
        { title }
      );
      return response.data;
    },
    deleteSession: async (sessionId: string): Promise<void> => {
      await client.delete(`/chat/session/${sessionId}`);
    },
    restoreSession: async (sessionId: string) => {
      const response = await client.patch<{ id: string; restored: boolean }>(
        `/chat/session/${sessionId}/restore`
      );
      return response.data;
    },
  },

  // Investor portal (public — no auth token sent)
  investorPortal: {
    verify: async (token: string) => {
      const response = await client.post<{
        name: string;
        firm: string;
        welcome_msg: string;
        cta_label: string;
        cta_url: string;
        sections: Record<string, boolean>;
      }>('/investor-portal/verify', { token });
      return response.data;
    },
  },
};

// AI Chat V1.1 Item 4 — Recent Chats response shapes
export interface ChatSessionSummary {
  id: string;
  title: string;
  deal_id: number | null;
  deal_name: string | null;
  language: string | null;
  started_at: string;
  last_message_at: string | null;
  message_count: number;
}

export interface ChatMessageRow {
  role: 'user' | 'assistant';
  content: string;
  // AI Chat V1.5 follow-up — array of {tool_name, result} from the
  // assistant's tool loop. Null for user turns and for assistant turns
  // that made no tool calls. Drives inline card rendering on session reload.
  tool_results: Array<{ tool_name: string; result: unknown }> | null;
  // Citations Phase 2 — array of {block_index, citations[]} entries
  // persisted from final_msg.content[i].citations. Null on user turns
  // and on pre-Phase-2 assistant rows. The frontend types the citation
  // payload through the discriminated union in features/chat/types.ts.
  citations: Array<{ block_index: number; citations: unknown[] }> | null;
  created_at: string;
}

// Service Health types
export interface ServiceStatus {
  name: string;
  description: string;
  endpoint: string;
  status: 'healthy' | 'degraded' | 'error' | 'unknown';
  last_success?: string;
  last_error?: string;
  consecutive_failures?: number;
  cache_age_hours?: number;
  models_checked?: number;
  api_key_configured?: boolean;
}

export interface ServicesHealthResponse {
  services: ServiceStatus[];
  overall_status: 'healthy' | 'partial' | 'degraded';
  checked_at: string;
  environment: string;
}

// Admin types
export interface AdminUser {
  id: number;
  email: string;
  company_name: string;
  industry?: string;
  job_title?: string;
  user_role: 'ADMIN' | 'OPS' | 'USER';
  subscription_tier?: string;
  created_at: string;
  last_login?: string;
}

export default api;
